import { Link, useNavigate } from '@tanstack/react-router';
import { MailPlus, RefreshCcw } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { JEV_LEVEL_LABELS, POOL_MATCH_EXCLUSIONS, POOL_MATCH_EXCLUSION_LABELS, jevLevelOf, jevReadingMicroUsdOf, type IcpReadState, type JobDetail, type PoolMatchRow } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Chip } from '../../../components/Chip';
import { DataTable } from '../../../components/data-table/DataTable';
import type { Column, SortSpec } from '../../../components/data-table/types';
import { Field } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { SkeletonLines } from '../../../components/Skeleton';
import { Stars } from '../../../components/Stars';
import { EmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { formatRelative } from '../../../lib/format';
import { EMPTY_SELECTION, type SelectionState } from '../../../lib/selection';
import { useCanView } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { experienceLabel } from '../../candidates/candidate-format';
import jevStyles from '../../ranking/components/jev.module.css';
import { ItemGlyphs } from '../../ranking/components/icp/IcpCells';
import { RankingNotices } from '../../ranking/components/RankingNotices';
import { useJevBudget } from '../../ranking/api/jev-budget-api';
import { jevBudgetButtonOf } from '../../ranking/api/use-jev-budget-gate';
import { poolsApi, usePoolMatches } from '../api/pools-api';
import { isMatched, poolReadNoticeOf, profileMatchOrder, starsOfMatch } from '../pool-profile-match';
import { announceInvite, usePoolRefresh } from './PoolPanels';
import styles from '../Pools.module.css';

const READ_BADGES: Readonly<Record<IcpReadState, { readonly label: string; readonly tone: 'success' | 'neutral' | 'warning' } | null>> = {
  read: { label: 'Read by Jev', tone: 'success' },
  waiting: { label: 'Not read yet', tone: 'neutral' },
  unreadable: { label: 'Can’t read: scan', tone: 'warning' },
  not_connected: { label: 'Jev isn’t connected', tone: 'neutral' },
  not_needed: null,
};

/**
 * POOL-03 — "N pool candidates match this job" with match; one-click invite (E12, §L17). The
 * server works out who matches and who is left out; this tab only shows it. N-19 lands here.
 * ADR 0062 (mockup M5): people are matched by the job's ideal candidate — facts at once, a match number once their CV is read.
 */
export function PoolMatchTab({ detail }: { readonly detail: JobDetail }): ReactNode {
  const canView = useCanView('talent_pool');
  const navigate = useNavigate();
  const jobId = detail.id;
  const matches = usePoolMatches(jobId, canView);
  const budget = useJevBudget(jobId).data;
  const refresh = usePoolRefresh();
  const show = useToasts((state) => state.show);
  const [sort, setSort] = useState<readonly SortSpec[]>([]);
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const [threshold, setThreshold] = useState<string | null>(null);
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  const [isRunning, setIsRunning] = useState(false);
  const [isReadStarting, setIsReadStarting] = useState(false);

  if (!canView) return <ForbiddenState resource="the talent pool" contact="Your admin" isInset />;
  if (matches.error && !matches.data) return <ServerErrorState onRetry={() => void matches.refetch()} />;
  if (!matches.data) return <SkeletonLines count={6} />;

  const view = matches.data;
  const isProfile = view.rankedBy === 'profile';
  if (isProfile && detail.profileSummary === null) {
    return (
      <EmptyState
        title="This job has no ideal candidate yet."
        body="The pool is matched against the job’s ideal candidate. Set one in job setup and matches appear here."
        action={
          detail.actions.includes('edit') ? (
            <Button variant="solid" onClick={() => void navigate({ to: '/jobs/$jobId/setup/$section', params: { jobId, section: 'profile' } })}>
              Set it in job setup
            </Button>
          ) : undefined
        }
        isInset
      />
    );
  }
  const byName = sort[0]?.columnId === 'name';
  const rows = byName
    ? [...view.matches].sort((a, b) => (sort[0]?.direction === 'asc' ? 1 : -1) * a.candidate.fullName.localeCompare(b.candidate.fullName))
    : isProfile
      ? profileMatchOrder(view.matches)
      : [...view.matches];
  const readNotice = view.jevRead ? poolReadNoticeOf(view.jevRead) : null;
  // ADR 0070 stage 2: reading pool members for this job is held to this job's Jev budget, like every spend.
  const jevLines = view.items?.filter((item) => item.kind === 'jev').length ?? 0;
  const readBudget = jevBudgetButtonOf(budget, view.jevRead ? jevReadingMicroUsdOf(view.jevRead.readableNow, jevLines) : 0, { isPriced: Boolean(view.jevRead) });
  const unmatched = isProfile ? rows.filter((row) => !isMatched(row)).length : 0;
  const chosen = selection.mode === 'all-matching' ? rows.filter((row) => !selection.excluded.has(row.candidate.id)) : rows.filter((row) => selection.ids.has(row.candidate.id));
  const left = POOL_MATCH_EXCLUSIONS.filter((reason) => view.excluded[reason] > 0).map((reason) => `${view.excluded[reason]} ${POOL_MATCH_EXCLUSION_LABELS[reason].toLowerCase()}`);

  const invite = async (people: readonly PoolMatchRow[]): Promise<void> => {
    if (people.length === 0) return;
    setBusy(new Set(people.map((row) => row.candidate.id)));
    try {
      const result = await poolsApi.invite({ jobId, candidateIds: people.map((row) => row.candidate.id) });
      await refresh();
      setSelection(EMPTY_SELECTION);
      announceInvite(show, result);
    } catch (cause) {
      show({ tone: 'danger', message: 'Nobody was invited', detail: errorMessage(cause) });
    } finally {
      setBusy(new Set());
    }
  };

  const saveThreshold = async (): Promise<void> => {
    if (threshold === null) return;
    const value = Number(threshold);
    if (!Number.isInteger(value) || value < 0 || value > 100) {
      show({ tone: 'warning', message: 'Use a whole number from 0 to 100' });
      return;
    }
    try {
      await poolsApi.setThreshold(jobId, value);
      await refresh();
      setThreshold(null);
      show({ tone: 'success', message: `Matching at ${value} or more` });
    } catch (cause) {
      show({ tone: 'danger', message: "The threshold wasn't changed", detail: errorMessage(cause) });
    }
  };

  const runAgain = async (): Promise<void> => {
    setIsRunning(true);
    try {
      await poolsApi.runMatch(jobId);
      await refresh();
    } catch (cause) {
      show({ tone: 'danger', message: "The match didn't run", detail: errorMessage(cause) });
    } finally {
      setIsRunning(false);
    }
  };

  /** ADR 0062 §5: "Read their CVs with Jev" reads up to 200 unread members in the background; the list re-orders as they are read. */
  const readCvs = async (): Promise<void> => {
    setIsReadStarting(true);
    try {
      await poolsApi.readCvs(jobId);
      await refresh();
    } catch (cause) {
      show({ tone: 'danger', message: "Jev didn't start reading", detail: errorMessage(cause) });
    } finally {
      setIsReadStarting(false);
    }
  };

  const rankOf = (row: PoolMatchRow): number | null => (isMatched(row) ? rows.filter(isMatched).indexOf(row) + 1 : null);

  const columns: readonly Column<PoolMatchRow>[] = [
    ...(isProfile && !byName ? [{ id: 'place', header: '#', isNumeric: true, defaultWidth: 56, cell: (row: PoolMatchRow) => rankOf(row) ?? '–' }] : []),
    {
      id: 'name',
      header: 'Name',
      isIdentity: true,
      isSortable: true,
      defaultWidth: 230,
      cell: (row) => (
        <span className={styles.stack}>
          <Link to="/candidates/$candidateId" params={{ candidateId: row.candidate.id }} className={styles.name}>
            {row.candidate.fullName}
          </Link>
          {row.candidate.currentTitle && <span className={styles.meta}>{row.candidate.currentTitle}</span>}
        </span>
      ),
    },
    {
      id: 'score',
      header: isProfile ? 'Profile match' : 'Match',
      isNumeric: !isProfile,
      defaultWidth: isProfile ? 190 : 110,
      cell: (row) => (isProfile ? <ProfileMatch row={row} /> : <CriteriaMatch row={row} />),
    },
    ...(isProfile
      ? [
          {
            id: 'read',
            header: 'CV read',
            defaultWidth: 150,
            cell: (row: PoolMatchRow) => {
              const badge = row.readState === null ? null : READ_BADGES[row.readState];
              return badge ? (
                <Chip tone={badge.tone} plain>
                  {badge.label}
                </Chip>
              ) : null;
            },
          },
        ]
      : []),
    ...(isProfile && view.items
      ? [
          {
            id: 'perItem',
            header: 'Per item: skills · habits · English · facts',
            defaultWidth: 240,
            cell: (row: PoolMatchRow) => (row.values && view.items ? <ItemGlyphs items={view.items} values={row.values} /> : null),
          },
        ]
      : []),
    { id: 'experience', header: 'Experience', defaultWidth: 110, cell: (row) => experienceLabel(row.candidate.totalExperienceMonths) },
    { id: 'city', header: 'City', defaultWidth: 110, cell: (row) => row.candidate.city },
    {
      id: 'pools',
      header: 'Pool and why',
      defaultWidth: 280,
      cell: (row) => (
        <span className={styles.stack}>
          {row.pools.map((pool) => (
            <span key={pool.id}>
              <Link to="/pools/$poolId" params={{ poolId: pool.id }} className={styles.name}>
                {pool.name}
              </Link>
              {pool.reason ? <span className={styles.meta}> — {pool.reason}</span> : null}
            </span>
          ))}
        </span>
      ),
    },
    { id: 'last', header: 'Last applied for', defaultWidth: 200, cell: (row) => row.lastJobTitle },
  ];

  return (
    <div className={styles.results}>
      {readNotice && (
        <RankingNotices
          notices={[{ kind: 'waiting', tone: 'info', title: readNotice.title, body: readBudget.blockedNote ? `${readNotice.body} ${readBudget.blockedNote}` : readNotice.body, progress: null, action: readNotice.buttonLabel ? { kind: 'rank', label: `${readNotice.buttonLabel}${readBudget.leftSuffix}` } : null }]}
          canRank={view.canInvite && readBudget.blockedNote === null}
          isRanking={isReadStarting || readNotice.isReading}
          onRank={() => void readCvs()}
        />
      )}
      <Card
        title="People in the talent pool who fit"
        meta={`${isProfile ? 'Matched against the job’s ideal candidate' : 'Scored the same way as applicants'}. Showing everyone at ${view.threshold} or more; do-not-rehire, cooldowns, expired consent and anyone who already applied are left out.`}
      >
        <div className={styles.matchBar}>
          {view.canSetThreshold && (
            <div className={styles.threshold}>
              <Field label="Match at or above">
                {({ inputId }) => (
                  <Input
                    id={inputId}
                    isNumeric
                    inputMode="numeric"
                    value={threshold ?? String(view.threshold)}
                    onChange={(event) => setThreshold(event.target.value)}
                    onBlur={() => void saveThreshold()}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') void saveThreshold();
                    }}
                  />
                )}
              </Field>
            </div>
          )}
          <Button variant="outline" icon={RefreshCcw} isPending={isRunning} onClick={() => void runAgain()}>
            Run again
          </Button>
          {view.canInvite && (
            <Button variant="solid" icon={MailPlus} disabled={chosen.length === 0} isPending={chosen.some((row) => busy.has(row.candidate.id))} onClick={() => void invite(chosen)}>
              Invite {chosen.length > 0 ? chosen.length : ''}
            </Button>
          )}
        </div>
        <p className={styles.excluded}>
          {view.matches.length} {view.matches.length === 1 ? 'match' : 'matches'} · {view.belowThreshold} below {view.threshold}
          {unmatched > 0 ? ` · ${unmatched} not matched yet: listed after the matched people, they join the order once their CV is read` : ''}
          {left.length > 0 ? ` · left out: ${left.join(', ')}` : ''}
          {view.lastRunAt ? ` · last run ${formatRelative(view.lastRunAt)}` : ''}
        </p>
      </Card>

      <DataTable
        screenId="pool-matches"
        label="Pool matches"
        columns={columns}
        rows={rows}
        getRowId={(row) => row.candidate.id}
        total={rows.length}
        sort={sort}
        onSortChange={setSort}
        selection={selection}
        onSelectionChange={setSelection}
        isSelectable={view.canInvite}
        onOpenRow={(row) => void navigate({ to: '/candidates/$candidateId', params: { candidateId: row.candidate.id } })}
        rowActions={
          view.canInvite
            ? (row) => (
                <Button variant="quiet" size="sm" icon={MailPlus} isPending={busy.has(row.candidate.id)} onClick={() => void invite([row])}>
                  Invite
                </Button>
              )
            : undefined
        }
        emptyState={
          <EmptyState
            title={`Nobody in a pool matches ${view.threshold} or more`}
            body={view.belowThreshold > 0 ? `${view.belowThreshold} pooled ${view.belowThreshold === 1 ? 'person matches' : 'people match'} lower. Lower the threshold above to see them.` : 'Add people to pools from All candidates or when rejecting strong candidates.'}
            action={
              <Button variant="outline" onClick={() => void navigate({ to: '/candidates' })}>
                Search candidates
              </Button>
            }
          />
        }
      />
    </div>
  );
}

/** The criteria's number and its flag, for a job still ranked by them. */
function CriteriaMatch({ row }: { readonly row: PoolMatchRow }): ReactNode {
  return (
    <span className={styles.stack}>
      <span className={styles.score}>{row.score}</span>
      {row.isMissingMustHave && (
        <Chip tone="warning" plain>
          Missing a must-have
        </Chip>
      )}
    </span>
  );
}

/** Mockup M5: level, number and stars once the CV is read (or the profile needs no reading); "Not matched yet" until then. */
function ProfileMatch({ row }: { readonly row: PoolMatchRow }): ReactNode {
  if (!isMatched(row)) {
    return (
      <span className={styles.stack}>
        <span className={styles.meta}>{row.readState === 'unreadable' ? 'Can’t be matched' : 'Not matched yet'}</span>
        <FitLine row={row} prefix="facts only: " />
      </span>
    );
  }
  const level = jevLevelOf(row.score);
  return (
    <span className={styles.stack}>
      <span className={jevStyles.level}>
        <span className={jevStyles.dot} data-level={level} aria-hidden />
        {JEV_LEVEL_LABELS[level]}
        <span className={jevStyles.levelNumber}>{row.score}</span>
        <Stars value={starsOfMatch(row.score, row.isMissingMustHave)} />
      </span>
      <FitLine row={row} prefix="" />
      {row.isMissingMustHave && (
        <Chip tone="warning" plain>
          Missing a must-have
        </Chip>
      )}
    </span>
  );
}

/** "fit 82 · covers 90%": how good what is known is, and how much of the ideal candidate is known. */
function FitLine({ row, prefix }: { readonly row: PoolMatchRow; readonly prefix: string }): ReactNode {
  if (row.fit === null || row.covers === null) return null;
  return (
    <span className={styles.meta}>
      {prefix}fit {row.fit} · covers {row.covers}%
    </span>
  );
}
